import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Loader2, CheckCircle } from "lucide-react";
import { Button } from "../ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DIVERGENCE_META = {
  divergencia:          { cls: "bg-amber-500/15 border-amber-500/30 text-amber-400", label: "DIVERGÊNCIA" },
  extra_na_planilha:    { cls: "bg-red-500/15 border-red-500/30 text-red-400",       label: "EXTRA NA PLANILHA" },
  faltando_na_planilha: { cls: "bg-blue-500/15 border-blue-500/30 text-blue-400",    label: "FALTANDO NA PLANILHA" },
};
const getTypeMeta = (type) => DIVERGENCE_META[type] || DIVERGENCE_META.faltando_na_planilha;

export const AnalisadorPersonagens = ({ getAuthHeaders }) => {
  const [sheetUrl, setSheetUrl] = useState("");
  const [charList, setCharList] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const analisar = async () => {
    if (!sheetUrl.trim() || !charList.trim()) {
      return toast.error("Preencha a URL e a lista de personagens");
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/tools/analyzer`,
        { sheet_url: sheetUrl, characters_list: charList },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
      setResult(res.data);
      if (res.data.total_divergences === 0) toast.success("Nenhuma divergência encontrada!");
      else toast.error(`${res.data.total_divergences} divergência(s) encontrada(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao analisar");
    } finally {
      setLoading(false);
    }
  };

  const charCount = charList.split("\n").filter(l => l.trim()).length;

  return (
    <div className="space-y-6" data-testid="analisador-personagens">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="space-y-4">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
            <label className="text-sm text-slate-300 font-medium block mb-2">
              Link da Planilha (Google Sheets)
            </label>
            <input
              type="text"
              value={sheetUrl}
              onChange={e => setSheetUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/..."
              className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-teal-400 text-sm"
              data-testid="analyzer-sheet-url"
            />
            <p className="text-xs text-slate-500 mt-2">
              A planilha deve estar compartilhada como pública. Será lida a aba "ROTEIRO" e a coluna "PERSONAGEM" (ou coluna D).
            </p>
          </div>

          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
            <label className="text-sm text-slate-300 font-medium block mb-2">
              Lista de Personagens (um por linha)
            </label>
            <textarea
              value={charList}
              onChange={e => setCharList(e.target.value)}
              placeholder={"MASCARADO\nCID PAIVA\nSUELI\nTINA OLIVEIRA\nDR. MARCOS"}
              className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-1 focus:ring-teal-400 text-sm resize-none"
              rows={10}
              data-testid="analyzer-char-list"
            />
            <p className="text-xs text-slate-500 mt-1">{charCount} personagem(ns)</p>
          </div>

          <Button
            onClick={analisar}
            disabled={loading}
            className="w-full bg-teal-600 hover:bg-teal-700 py-5 text-base"
            data-testid="analyzer-run-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Search className="w-5 h-5 mr-2" />}
            {loading ? "Analisando..." : "Analisar Divergências"}
          </Button>
        </div>

        {/* Results */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 h-fit" data-testid="analyzer-results">
          {!result ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Search className="w-14 h-14 text-slate-700 mb-3" />
              <p className="text-slate-500 text-sm">
                Cole o link da planilha e a lista de personagens, depois clique em "Analisar"
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-slate-900 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-400">Aba</p>
                  <p className="text-sm text-white font-medium truncate">{result.sheet_name}</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-400">Coluna</p>
                  <p className="text-sm text-white font-medium truncate">{result.column_used}</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-400">Na Planilha</p>
                  <p className="text-sm text-teal-400 font-bold">{result.unique_sheet_names}</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-400">Divergências</p>
                  <p className={`text-sm font-bold ${result.total_divergences > 0 ? "text-red-400" : "text-emerald-400"}`}>
                    {result.total_divergences}
                  </p>
                </div>
              </div>

              {/* Divergences */}
              {result.total_divergences === 0 ? (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-6 text-center">
                  <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                  <p className="text-emerald-400 font-bold">Nenhuma divergência encontrada!</p>
                  <p className="text-slate-400 text-sm mt-1">
                    Todos os personagens conferem com a planilha.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                  {result.divergences.map((d, i) => {
                    const meta = getTypeMeta(d.type);
                    const divKey = `${d.type}-${d.line || ""}-${d.sheet_value || d.expected || ""}-${i}`;
                    return (
                      <div
                        key={divKey}
                        className={`border rounded-lg p-3 ${meta.cls}`}
                        data-testid={`divergence-${i}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold uppercase tracking-wider">{meta.label}</span>
                          {d.line && <span className="text-xs opacity-70">Linha {d.line}</span>}
                        </div>
                        {d.type === "divergencia" && (
                          <div className="space-y-1 mt-2">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-slate-400 text-xs w-16 shrink-0">Planilha:</span>
                              <code className="bg-slate-900/50 px-2 py-0.5 rounded font-mono text-red-300">{d.sheet_value}</code>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-slate-400 text-xs w-16 shrink-0">Esperado:</span>
                              <code className="bg-slate-900/50 px-2 py-0.5 rounded font-mono text-emerald-300">{d.expected}</code>
                            </div>
                            {d.chars_diff && <p className="text-xs opacity-70 mt-1">{d.chars_diff}</p>}
                          </div>
                        )}
                        {d.type === "extra_na_planilha" && (
                          <p className="text-sm mt-1">
                            <code className="bg-slate-900/50 px-2 py-0.5 rounded font-mono">{d.sheet_value}</code>{" "}
                            está na planilha mas não na lista
                          </p>
                        )}
                        {d.type === "faltando_na_planilha" && (
                          <p className="text-sm mt-1">
                            <code className="bg-slate-900/50 px-2 py-0.5 rounded font-mono">{d.expected}</code>{" "}
                            está na lista mas não na planilha
                          </p>
                        )}
                        {d.detail && d.type === "divergencia" && (
                          <p className="text-xs mt-1 opacity-60">{d.detail}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
